/**
 * MonthWeekRow Component
 *
 * Renders a single week row in the month view grid.
 * Contains week label, day cells, padding cells, and weekly sum.
 */

import { MonthDayCell } from "@/components/schedule/MonthDayCell";
import {
  calculateTotalHours,
  calculateAbsenceHours,
  calculateAbsenceFill,
  calculateHardBookingHours,
  getBookingStatus,
} from "@/utils/booking";
import { formatDecimal } from "@/utils/number/formatters";
import classNames from "classnames";
import { formatAPIDate } from "@/utils/date";
import { isToday } from "date-fns";
import React from "react";
import styles from "../Schedule.module.css";
import type { MonthWeekRowProps } from "../types";

/**
 * Get color class for weekly sum
 */
const getWeeklySumColorClass = (
  hours: number,
  capacity: number,
  isSoftBooking: boolean,
): string => {
  if (hours === 0) return styles.grey;
  if (isSoftBooking) return styles.green;
  if (capacity === 0) return styles.grey;
  if (hours > capacity) return styles.red;
  if (hours >= capacity * 0.72) return styles.green;
  return styles.orange;
};

/**
 * Calculate weekly capacity accounting for weekends and public holidays
 */
const calculateWeekCapacity = (
  days: { isWeekend: boolean; isRedDay: boolean }[],
  hoursInDay: number,
): number => {
  const workingDays = days.filter(
    (day) => !day.isWeekend && !day.isRedDay,
  ).length;
  return workingDays * hoursInDay;
};

/**
 * Get day status for soft booking mode (simplified)
 */
const getSoftBookingStatus = (dayHours: number): "fullyBooked" | "free" => {
  return dayHours > 0 ? "fullyBooked" : "free";
};

export const MonthWeekRow: React.FC<MonthWeekRowProps> = React.memo(
  ({
    week,
    employee,
    daysPerWeek,
    hideWeekend,
    hoursInDay,
    hideAggregate,
    selectMode,
    selectedCardId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used by React.memo to trigger re-render when selection changes (Bug #9022)
    selectionCount: _selectionCount,
    isSelected,
    isInRangePreview,
    setRangeHoverTarget,
    isInteractive,
    getBookingsForDay,
    getCardForDay,
    getWeeklySum,
    handleCardClick,
    onWeekLabelClick,
    onCardClick,
  }) => {
    // Calculate effective padding when weekends are hidden
    const effectivePadLeft = hideWeekend
      ? Math.min(week.padLeft, 5)
      : week.padLeft;

    const weekdayCount = week.days.filter((d) => !d.isWeekend).length;
    const effectivePadRight = hideWeekend
      ? Math.max(0, daysPerWeek - effectivePadLeft - weekdayCount)
      : week.padRight;

    return (
      <div
        key={week.weekNumber}
        className={styles.monthWeekRow}
        style={{
          gridTemplateColumns: `auto repeat(${daysPerWeek}, 1fr) auto`,
        }}
      >
        {/* Week Label */}
        <div className={styles.weekLabel}>
          <span onClick={() => onWeekLabelClick(week)}>
            Uke {week.weekNumber}
          </span>
        </div>

        {/* Padding cells (left) */}
        {Array.from({ length: effectivePadLeft }).map((_, i) => (
          <div key={`pad-left-${i}`} className={styles.padCell} />
        ))}

        {/* Day cells */}
        {week.days.map((day) => {
          if (hideWeekend && day.isWeekend) {
            return null;
          }

          const dayBookings = getBookingsForDay(employee, day.date);
          const dayHours = calculateTotalHours(dayBookings);
          const absenceHours = calculateAbsenceHours(dayBookings);
          const hardBookingHours = calculateHardBookingHours(dayBookings);
          const cardData = getCardForDay(employee, day.date);
          const cardSelected = selectMode
            ? isSelected(cardData.id)
            : cardData.id === selectedCardId;

          const status = hideAggregate
            ? getSoftBookingStatus(dayHours)
            : getBookingStatus(hardBookingHours, absenceHours, hoursInDay);

          const absenceFill = hideAggregate
            ? 0
            : calculateAbsenceFill(absenceHours, hoursInDay);

          return (
            <MonthDayCell
              key={day.date.toISOString()}
              hours={dayHours}
              date={day.date}
              status={status}
              absenceFill={absenceFill}
              isToday={isToday(day.date)}
              isWeekend={day.isWeekend}
              isRedDay={day.isRedDay}
              selected={cardSelected}
              selectable={selectMode && isInteractive}
              inRangePreview={selectMode && isInRangePreview(cardData.id)}
              showCheckbox={false}
              onClick={
                isInteractive
                  ? (e) => {
                      handleCardClick(
                        cardData.id,
                        {
                          shift: e.shiftKey,
                          ctrl: e.ctrlKey,
                          meta: e.metaKey,
                        },
                        onCardClick,
                      );
                    }
                  : undefined
              }
              onMouseEnter={
                selectMode && isInteractive
                  ? (e) => {
                      // Only set range target when shift is held (Bug #9023)
                      if (e.shiftKey) {
                        setRangeHoverTarget(cardData.id);
                      } else {
                        setRangeHoverTarget(null);
                      }
                    }
                  : undefined
              }
              data-testid={`month-day-cell-${employee.id}-${formatAPIDate(day.date)}`}
            />
          );
        })}

        {/* Padding cells (right) */}
        {Array.from({ length: effectivePadRight }).map((_, i) => (
          <div key={`pad-right-${i}`} className={styles.padCell} />
        ))}

        {/* Weekly Sum */}
        <div
          className={classNames(
            styles.weekSum,
            getWeeklySumColorClass(
              getWeeklySum(employee, week.startDate),
              calculateWeekCapacity(week.days, hoursInDay),
              hideAggregate,
            ),
          )}
        >
          <span>{formatDecimal(getWeeklySum(employee, week.startDate))} T</span>
        </div>
      </div>
    );
  },
);

MonthWeekRow.displayName = "MonthWeekRow";

export default MonthWeekRow;
